import React, { useState } from "react";
import {
  MultiCheckBoxComponent,
  ValidationRule,
  FlexDirection,
} from "../../services/types/ComponentTypes";

type MultiCheckBoxValueItem =
  | string
  | number
  | boolean
  | Record<string, any>;

interface MultiCheckBoxProps {
  config: MultiCheckBoxComponent;
  value?: MultiCheckBoxValueItem[];
  onChange?: (name: string, value: MultiCheckBoxValueItem[]) => void;
}

const isSoftDeletedRow = (item: any): boolean =>
  item?.soft_delete === 1 ||
  item?.soft_delete === "1" ||
  item?.soft_delete === true;

const getLookupIdFromItem = (
  item: MultiCheckBoxValueItem,
  jsonAttr: string
): string => {
  if (item == null) return "";
  if (typeof item !== "object") return String(item);
  // Never fall back to row.id — that is the persisted child PK, not the lookup id.
  const raw = (item as any)[jsonAttr] ?? (item as any).value;
  return raw == null || raw === "" ? "" : String(raw);
};

export const MultiCheckBox: React.FC<MultiCheckBoxProps> = ({
  config,
  value = [],
  onChange,
}) => {
  const [error, setError] = useState<string>("");
  const [selectedValues, setSelectedValues] = useState<
    (string | number | boolean)[]
  >([]);

  const jsonAttr = (config as any).Json_Attribute || "id";
  // Persisted MULTI_CHECKBOX subforms (e.g. work requested) keep row objects with ids.
  const preserveRowObjects = Boolean(
    (config as any).Json_Attribute ||
      (config as any).TableName ||
      (config as any).tableName
  );

  // Sync with value prop
  React.useEffect(() => {
    let newValues: (string | number | boolean)[] = [];

    if (value && value.length > 0) {
      if (typeof value[0] === "object" && value[0] !== null) {
        newValues = value
          .filter((item) => !isSoftDeletedRow(item))
          .map((item: any) => getLookupIdFromItem(item, jsonAttr))
          .filter((id) => id !== "");
      } else {
        newValues = (value as (string | number | boolean)[]).map(String);
      }
    } else if (config.defaultValue) {
      newValues = (config.defaultValue as any[]).map(String);
    }

    setSelectedValues(newValues);
  }, [value, jsonAttr]);

  const fieldName =
    config.name || (config as any).BindValue || (config as any).bindValue || "";

  const validateData = (
    dataToValidate: (string | number | boolean)[]
  ): string => {
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (dataToValidate.length === 0)
            return rule.message || "At least one option must be selected";
          break;
        case ValidationRule.MIN_VALUE:
          if (dataToValidate.length < (rule.value as number))
            return rule.message || `Minimum ${rule.value} selections required`;
          break;
        case ValidationRule.MAX_VALUE:
          if (dataToValidate.length > (rule.value as number))
            return rule.message || `Maximum ${rule.value} selections allowed`;
          break;
      }
    }
    return "";
  };

  const handleSelectionChange = (
    optionValue: string | number | boolean,
    checked: boolean
  ) => {
    let newSelectedValues: (string | number | boolean)[];
    const stringOptionValue = String(optionValue);

    if (checked) {
      if (!selectedValues.some((v) => String(v) === stringOptionValue)) {
        if (
          config.maxSelections &&
          selectedValues.length >= config.maxSelections
        ) {
          return;
        }
        newSelectedValues = [...selectedValues, stringOptionValue];
      } else {
        return;
      }
    } else {
      newSelectedValues = selectedValues.filter(
        (val) => String(val) !== stringOptionValue
      );
    }

    setSelectedValues(newSelectedValues);
    const validationError = validateData(newSelectedValues);
    setError(validationError);

    if (!preserveRowObjects) {
      onChange?.(fieldName, newSelectedValues);
      return;
    }

    // Preserve persisted row objects (id + lookup attr) like MULTI_SUB_LIST.
    const prevRows: Record<string, any>[] = Array.isArray(value)
      ? value.map((item) => {
          if (item != null && typeof item === "object" && !Array.isArray(item)) {
            return { ...(item as Record<string, any>) };
          }
          const lookupId = String(item ?? "");
          return lookupId ? { [jsonAttr]: lookupId, act: "add" } : null;
        }).filter(Boolean) as Record<string, any>[]
      : [];

    let nextRows = [...prevRows];
    const matchIdx = nextRows.findIndex(
      (row) => getLookupIdFromItem(row, jsonAttr) === stringOptionValue
    );

    if (checked) {
      if (matchIdx >= 0) {
        const existing = { ...nextRows[matchIdx] };
        delete existing.soft_delete;
        if (existing.id) {
          existing.act = existing.act === "add" ? "add" : "edit";
        } else {
          existing.act = "add";
        }
        nextRows[matchIdx] = existing;
      } else {
        nextRows.push({ [jsonAttr]: stringOptionValue, act: "add" });
      }
    } else if (matchIdx >= 0) {
      const existing = nextRows[matchIdx];
      const persistedId =
        existing?.id != null && String(existing.id).trim() !== ""
          ? String(existing.id).trim()
          : "";
      if (persistedId) {
        nextRows[matchIdx] = {
          ...existing,
          soft_delete: 1,
          act: "edit",
        };
      } else {
        nextRows = nextRows.filter((_, i) => i !== matchIdx);
      }
    }

    onChange?.(fieldName, nextRows);
  };

  const containerStyles: React.CSSProperties = {
    backgroundColor: config.style?.backgroundColor || "transparent",
    padding: config.style?.padding || 16,
    borderRadius: config.style?.borderRadius || 8,
    margin: config.style?.margin || 0,
  };

  return (
    <div
      className={`border ${error ? "border-2 border-danger-500 dark:border-danger-500" : "border-gray-300 dark:border-gray-600"
        }`}
      style={containerStyles}
    >
      {config.label && (
        <label className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <div
        className={`flex ${config.direction === FlexDirection.ROW
          ? "flex-row flex-wrap"
          : "flex-row flex-wrap"
          } gap-3`}
      >
        {!config.options || !Array.isArray(config.options) || config.options.length === 0 ? (
          <div className="text-gray-500 dark:text-gray-400 text-sm py-2">
            {config.lookupName || config.lookupName
              ? "Loading options..."
              : "No options available"}
          </div>
        ) : (
          config.options.map((option, index) => {
            const isSelected = selectedValues.some(val => String(val) === String(option.value));
            const isDisabled = option.disabled || config.disabled;

            return (
              <div
                key={index}
                className={`flex items-center p-2 rounded-md transition-colors duration-200 ${isDisabled
                  ? "cursor-not-allowed opacity-70"
                  : "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                onClick={() =>
                  !isDisabled &&
                  handleSelectionChange(option.value, !isSelected)
                }
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) =>
                    !isDisabled &&
                    handleSelectionChange(option.value, e.target.checked)
                  }
                  disabled={isDisabled}
                  className="w-4 h-4 mr-2 cursor-pointer accent-success-600 dark:accent-success-500"
                />
                <span className="text-sm font-normal text-gray-900 dark:text-gray-100 cursor-pointer select-none font-sans">
                  {option.label}
                </span>
              </div>
            );
          })
        )}
      </div>

      {error && (
        <div className="text-danger-500 dark:text-danger-400 text-sm mt-3 flex items-center">
          <span className="mr-1">⚠️</span>
          {error}
        </div>
      )}

      {config.helperText && !error && (
        <div className="text-gray-500 dark:text-gray-400 text-sm mt-3">{config.helperText}</div>
      )}

      {config.maxSelections && (
        <div className="text-gray-500 dark:text-gray-400 text-xs mt-2 text-right">
          {selectedValues.length}/{config.maxSelections} selected
        </div>
      )}
    </div>
  );
};
